import React, { useCallback, useState } from 'react';
import { Input, Button, Space, Typography, Alert, Spin, message } from 'antd';
import { SearchOutlined, CopyOutlined } from '@ant-design/icons';
import lib from '../../../assets/utils/lib';

const { Text, Paragraph } = Typography;

const PreviewTab = ({ partnerId }) => {
  const [checkinId, setCheckinId] = useState('');
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    const id = checkinId.trim();
    if (!id) return;
    setLoading(true);
    setError(null);
    setPayload(null);
    try {
      const resp = await lib.previewPaAgentPayload(partnerId, id);
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        const code = body?.code;
        setError(
          code === 'preview.disabled'
            ? 'Preview is disabled in this environment.'
            : (body?.message || 'Could not build preview'),
        );
        return;
      }
      setPayload(body?.data || body);
    } catch (e) {
      setError(e?.message || 'Could not build preview');
    } finally {
      setLoading(false);
    }
  }, [checkinId, partnerId]);

  const payloadString = payload
    ? (() => { try { return JSON.stringify(payload, null, 2); } catch { return String(payload); } })()
    : '';

  const onCopy = async () => {
    if (!payloadString) return;
    try {
      await navigator.clipboard.writeText(payloadString);
      message.success('Copied');
    } catch (e) {
      message.error('Copy failed');
    }
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Dry-run payload builder"
        description="Build the exact SaaSPro payload that the system would enqueue for a given check-in. No outbox row is created. Useful for QA on real encounters."
      />

      <Space style={{ marginBottom: 12 }}>
        <Input
          allowClear
          placeholder="Checkin id"
          prefix={<SearchOutlined />}
          style={{ width: 280 }}
          value={checkinId}
          onChange={(e) => setCheckinId(e.target.value)}
          onPressEnter={run}
        />
        <Button type="primary" onClick={run} loading={loading} disabled={!checkinId.trim()}>
          Build payload
        </Button>
      </Space>

      {loading && <Spin />}

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      )}

      {payloadString && (
        <>
          <Space style={{ marginBottom: 6 }}>
            <Text strong>Payload</Text>
            <Button size="small" icon={<CopyOutlined />} onClick={onCopy}>Copy JSON</Button>
          </Space>
          <Paragraph
            style={{
              background: '#0b1021',
              color: '#dbe4ff',
              borderRadius: 6,
              padding: 12,
              maxHeight: 520,
              overflow: 'auto',
              fontFamily: 'ui-monospace,Menlo,Consolas,monospace',
              fontSize: 12,
              whiteSpace: 'pre',
            }}
          >
            {payloadString}
          </Paragraph>
        </>
      )}
    </div>
  );
};

export default PreviewTab;
